import type { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';

import { useMemo, useState, useEffect, useCallback, createContext } from 'react';

import { useDynatraceIdentifyUser } from 'src/hooks/useDynatrace';

import { SESSION_STORAGE_KEYS } from 'src/utils/constants';

import { useUser } from './hooks/useUser';
import { useGetUser } from './hooks/useGetUser';
import { useGetTenants } from './hooks/useGetTenants';
import { useUpdateRole } from './hooks/useUpdateRole';
import { useCreateSession } from './hooks/useCreateSession';

import type { User } from './types';

type AuthContext = {
  user: User | undefined;
  refetchUserId: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
  isLoading: boolean;
  hasRoles: (role: string[]) => boolean;
  hasPermissions: (permissions: string[]) => boolean;
};

type AuthContextProviderProps = {
  children: React.ReactNode;
};

export const AuthContext = createContext<AuthContext | undefined>(undefined);

function AuthProvider({ children }: AuthContextProviderProps) {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const {
    user: { firstName, lastName, email },
    isAuthTokenSet,
  } = useUser();

  // Set the current user in Dynatrace (when available)
  useDynatraceIdentifyUser(email);

  const { updateRole } = useUpdateRole();
  const { user: userData, refetchUserId } = useGetUser(isAuthTokenSet);
  const { tenants } = useGetTenants(isAuthTokenSet);
  const createSession = useCreateSession();

  const hasRoles = useCallback(
    (roles: string[]) => !!user?.roles?.some((r) => roles.includes(r.role_name)),
    [user]
  );

  const hasPermissions = useCallback(
    (permissions: string[]) =>
      !!user?.roles?.some((r) => permissions.some((p) => r.permissions.includes(p))),
    [user]
  );

  const handleUserUpdate = () => {
    // TODO: need a better approach.
    // add a check to see if userData is undefined
    if (!userData) {
      console.error('Cannot update user: missing user data');
      setIsLoading(false);
      return;
    }

    const tenantId = tenants?.find((tenant: { name: string }) => tenant.name === 'default')?.id;
    const userId = userData?.id;
    const emailId = email?.split('@')[0];

    // To avoid updating the role every time the page refreshes in the same session,
    // skip the update if the user role id is already set in the session storage.
    if (!sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_ROLE_SYNC_TIMESTAMP) && tenantId) {
      updateRole(
        { tenant_id: tenantId },
        {
          onSuccess: (data) => {
            setUser({
              firstName,
              lastName,
              id: userId,
              username: emailId,
              roles: data.roles,
            });
            setIsLoading(false);
          },
        }
      );
    } else {
      setUser({
        firstName,
        lastName,
        id: userId,
        username: emailId,
        roles: userData?.roles,
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    try {
      if (userData && tenants?.length) {
        const tenantId = tenants.find((tenant: { name: string }) => tenant.name === 'default')?.id;
        if (tenantId) {
          if (!sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_ID)) {
            createSession(
              { user_id: userData?.id, tenant_id: tenantId },
              {
                onSuccess: () => {
                  handleUserUpdate();
                },
              }
            );
          } else {
            handleUserUpdate();
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, tenants]);

  const values = useMemo(
    () => ({ user, refetchUserId, isLoading, hasRoles, hasPermissions }),
    [refetchUserId, user, isLoading, hasRoles, hasPermissions]
  );

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
